import styled from '@emotion/styled';
import { BodyTextXSmall, pxToRem } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { ReactNode, FC } from 'react';
import Linkify from 'react-linkify';

import { MessageResourceInput } from 'src/generated/webapp_gql';
import { truncateText } from 'src/shared/helpers/truncateText';
import { Conversation } from 'src/shared/messaging-kit/types';

import { Avatar } from './Avatar';
import { MessageResource } from './MessageResource';

export interface MessageContentProps {
    sender: Conversation['coach'] | Conversation['member'];
    content: ReactNode;
    messageResources: MessageResourceInput[] | undefined;
    sentAt: string;
}

export const MessageContentTestId = 'MessageContent.TestId';

const getLinkifyComponentDecorator = (
    decoratedHref: string,
    decoratedText: string,
    key: number,
) => (
    <StyledLink href={decoratedHref} key={key} target="_blank" rel="noopener noreferrer">
        {truncateText(decoratedText, { maxLength: 140, headLength: 80, tailLength: 60 })}
    </StyledLink>
);

export const MessageContent: FC<MessageContentProps> = ({
    sender,
    content,
    sentAt,
    messageResources,
}) => {
    const sentAtDate = new Date(sentAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <StyledMessageContentRow data-testid={MessageContentTestId}>
            <StyledAvatarContainer>
                <Avatar user={sender} diameter={30} />
            </StyledAvatarContainer>
            <StyledMessageContentWrapper>
                <StyledMessageContentSender>{`${sender?.firstName} ${sender?.lastName}`}</StyledMessageContentSender>
                <StyledMessageContentContainer>
                    <Linkify componentDecorator={getLinkifyComponentDecorator}>{content}</Linkify>
                </StyledMessageContentContainer>
                <MessageResource resources={messageResources} />
                <StyledSentAt>
                    <BodyTextXSmall>{sentAtDate}</BodyTextXSmall>
                </StyledSentAt>
            </StyledMessageContentWrapper>
        </StyledMessageContentRow>
    );
};

const StyledMessageContentRow = styled.div({
    padding: pxToRem(10, 0),
    display: 'flex',
});

const StyledAvatarContainer = styled.div({
    padding: pxToRem(5, 5, 5, 0),
});

const StyledMessageContentSender = styled.div({
    fontWeight: 'bold',
    marginBottom: pxToRem(6),
    color: colors.gray85,
});

const StyledMessageContentContainer = styled.div`
    color: ${colors.gray75};
    line-height: ${pxToRem(20)};
    white-space: pre-wrap;
    margin-bottom: ${pxToRem(12)};
`;

const StyledMessageContentWrapper = styled.div`
    margin: ${pxToRem(5)};
    width: 100%;
`;

const StyledSentAt = styled.div`
    color: ${colors.gray40};
`;

const StyledLink = styled.a`
    word-break: break-all;
`;
